import type { UserRole } from "../../types/auth";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { providerApi } from "@/services/api/providerApi";
import { useAuthStore } from "@/stores/authStore";
import { useI18nStore } from "@/stores/i18nStore";
import { i18nToast } from "@/utils/i18nToast";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export const HealthcareProviderAdminRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuthStore();
  const { t } = useI18nStore();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    organizationName: "",
    phoneNumber: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response =
        await providerApi.registerHealthcareProviderAdmin(formData);

      // Update auth store with new user and token
      localStorage.setItem(
        "healthpass-auth",
        JSON.stringify({
          token: response.token,
          user: response.user,
        }),
      );

      updateUser({
        ...response.user,
        role: response.user.role as UserRole,
      });

      i18nToast.registrationSuccess(formData.firstName);
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Registration error:", error);
      i18nToast.registrationFailed();
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-green-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t.auth2HealthcareProvider}
            </h1>
            <p className="text-gray-600">{t.auth2CreateAdminAccount}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="provider-admin-firstname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t.firstName} *
                </label>
                <input
                  id="provider-admin-firstname"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="provider-admin-lastname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t.lastName} *
                </label>
                <input
                  id="provider-admin-lastname"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="provider-admin-email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.email} *
              </label>
              <input
                id="provider-admin-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label
                htmlFor="provider-admin-password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.password} *
              </label>
              <input
                id="provider-admin-password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                minLength={6}
              />
            </div>

            <div>
              <label
                htmlFor="provider-admin-organization"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.auth2OrganizationRequired}
              </label>
              <input
                id="provider-admin-organization"
                type="text"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleInputChange}
                placeholder={t.auth2OrganizationPlaceholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label
                htmlFor="provider-admin-phone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.phone}
              </label>
              <input
                id="provider-admin-phone"
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="bg-primary-50 p-4 rounded-lg">
              <h4 className="font-medium text-primary-900 mb-2">
                {t.auth2ProviderAdminCan}
              </h4>
              <ul className="text-sm text-primary-800 space-y-1">
                <li>• {t.auth2ProviderPermHealthData}</li>
                <li>• {t.auth2ProviderPermInviteStaff}</li>
                <li>• {t.auth2ProviderPermCertificates}</li>
                <li>• {t.auth2ProviderPermConsultations}</li>
              </ul>
            </div>

            <div className="flex justify-between items-center pt-6">
              <Link
                to="/traveler/login"
                className="text-primary-600 hover:text-primary-700 text-sm"
              >
                {t.auth2AlreadyRegisteredSignIn}
              </Link>

              <button
                type="submit"
                disabled={isLoading}
                className="bg-primary-600 text-white px-8 py-3 rounded-md font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? t.auth2Registering : t.register}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/auth/register"
                className="text-gray-600 hover:text-gray-700 text-sm"
              >
                {t.auth2RegisterAsRegularUser}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
